import React, { useEffect, useState } from 'react'
import {
  Box,
  Stack,
  Button as ChakraButton,
  Icon,
  FormControl,
  FormErrorMessage,
  FormLabel,
  InputLeftElement,
  InputGroup
} from '@chakra-ui/react'
import {
  useAlert,
  Button,
  LabeledInput,
  LabeledSelect,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Select,
  theme,
  Input
} from '@apex/component-library'
import { FaQuestionCircle } from 'react-icons/fa'
import { useFormikContext } from 'formik'
import { PositionJournalValues, SecurityCodeTypeText } from '../../containers/PositionJournals/types'
import { MessageTypes } from '../../types/MessageTypes'
import { NO_FIRM_ACCOUNT } from '../../constants'
import { MAX_COLUMN_WIDTH, MAX_FORM_WIDTH } from '../../utils'
import { JournalTypeText } from '../../containers/PositionJournals/types'
import { withFormikDevtools } from 'formik-devtools-extension'
import { FromAccount } from '../FromAccount'
import { AccountData, AccountDetailResponsePayload, SearchAccountsResponsePayload } from '@Types'
import { numberInputFormatter } from '../../utils'
import { useFormatNumber } from '../../utils'
import ToAccountLookup from '../ToAccountLookup'
import { PositionJournalPreviewModal } from './PositionJournalPreviewModal'
import { buildToAccountDisplayText } from '../../utils/optionBuilders'

export interface PositionJournalFormProps {
  accountDetails?: AccountDetailResponsePayload | null
  account: SearchAccountsResponsePayload
  hasSubmitPermission: boolean
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

const PositionJournalForm = ({
  accountDetails,
  account,
  hasSubmitPermission,
  isOpen,
  onOpen,
  onClose
}: PositionJournalFormProps) => {
  const { setAlert, removeAlert, alerts } = useAlert()
  const [noFirmAccountAlertId, setNoFirmAccountAlertId] = useState<string | null>(null)
  const [isToAccountValid, setIsToAccountValid] = useState(false)
  const formik = useFormikContext<PositionJournalValues>()
  withFormikDevtools(formik)
  const numberFormatter = useFormatNumber()
  const { values, isValid } = formik
  const hasNoFirmAccountAlert = alerts.some((alert) => alert.id === 'no-firm-account')
  const [accountLookupTitle, setAccountLookupTitle] = useState('')

  useEffect(() => {
    if (accountDetails?.accountRegistration !== 'FIRM_ACCOUNT') {
      if (!noFirmAccountAlertId) {
        const noFirmAccountMessage = <span>{NO_FIRM_ACCOUNT}</span>
        const { id } = setAlert({
          id: 'no-firm-account',
          title: 'Not Available',
          description: noFirmAccountMessage,
          isClosable: true,
          status: MessageTypes.error
        })
        setNoFirmAccountAlertId(id)
      }
    }
    return () => {
      if (noFirmAccountAlertId && hasNoFirmAccountAlert) {
        removeAlert(noFirmAccountAlertId as string)
      }
    }
  }, [setAlert, setNoFirmAccountAlertId, removeAlert, noFirmAccountAlertId, hasNoFirmAccountAlert, accountDetails])

  const {
    journalType,
    toAccount,
    securityCodeType,
    securityId,
    quantityDisplay,
    amountDisplay,
    sourceAccountDescription,
    sourceAccountDescription2,
    destinationAccountDescription,
    destinationAccountDescription2
  } = values

  const fromAccountDisplay = `${account.accountNumber} (${account.accountTitle})`

  const handleLookupSubmit = (toAccountData?: AccountData) => {
    if (toAccountData) {
      setIsToAccountValid(true)
      setAccountLookupTitle(toAccountData?.accountTitle)
    } else {
      setIsToAccountValid(false)
      setAccountLookupTitle('')
    }
  }

  return (
    <Box as='form' pb={4} maxW={MAX_FORM_WIDTH} w='100%'>
      <Stack spacing={6}>
        <Box maxW={MAX_COLUMN_WIDTH}>
          <FormControl>
            <FormLabel>
              Journal Type *{' '}
              <Popover
                placement='top-start'
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [-5, 10]
                    }
                  }
                ]}
                trigger='click'
                defaultIsOpen={false}
              >
                <PopoverTrigger>
                  <ChakraButton
                    aria-label='journal type popover'
                    variant='link'
                    h='1rem'
                    d='inline-block'
                    minW='1rem'
                    lineHeight={0}
                    border={0}
                    rounded='full'
                  >
                    <Icon as={FaQuestionCircle} color='black'>
                      Trigger Popover
                    </Icon>
                  </ChakraButton>
                </PopoverTrigger>
                <PopoverContent color='black'>
                  <PopoverArrow />
                  <PopoverBody>Reward is the only journal type available at this time.</PopoverBody>
                </PopoverContent>
              </Popover>
            </FormLabel>
            <Select
              name='journalType'
              aria-label='journal type'
              value={journalType}
              onChange={(e) => formik.setFieldValue('journalType', e.target.value)}
              onBlur={formik.handleBlur}
              isRequired
            >
              <option value='REWARD'>{JournalTypeText.REWARD}</option>
            </Select>
          </FormControl>
        </Box>
        <Box>
          <FromAccount displayedValue={fromAccountDisplay} />
        </Box>
        <ToAccountLookup
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          errorMessage={formik.errors.toAccount}
          touched={formik.touched.toAccount}
          fromAccount={account.accountNumber}
          toAccount={toAccount}
          checkThirdParty={false}
          onLookupSubmit={handleLookupSubmit}
        />
        <Stack direction='row' spacing={8}>
          <LabeledSelect
            name='securityCodeType'
            aria-label='security code type'
            value={securityCodeType}
            label='Security Code Type'
            onChange={(e) => formik.setFieldValue('securityCodeType', e.target.value)}
            onBlur={formik.handleBlur}
            isRequired
          >
            <option value='TICKER_SYMBOL'>{SecurityCodeTypeText.TICKER_SYMBOL}</option>
            <option value='CUSIP'>{SecurityCodeTypeText.CUSIP}</option>
            <option value='OCC_SYMBOL'>{SecurityCodeTypeText.OCC_SYMBOL}</option>
          </LabeledSelect>
          <LabeledInput
            label='Security ID'
            name='securityId'
            isRequired
            value={securityId}
            onChange={(e) => formik.setFieldValue('securityId', e.target.value)}
            onBlur={formik.handleBlur}
            errorMessage={formik.errors?.securityId}
            isInvalid={formik.touched.securityId && !!formik.errors?.securityId}
          />
        </Stack>
        <Stack direction='row' spacing={8}>
          <LabeledInput
            label='Quantity'
            name='quantity'
            isRequired
            value={quantityDisplay}
            onChange={(e) => {
              let inputValue
              if (securityCodeType === 'OCC_SYMBOL') {
                inputValue = e.target.value.replace(/[^0-9]*/g, '') // numeric values only
              } else {
                inputValue = e.target.value.replace(/[^0-9.]|\.(?=.*\.)/g, '') // numeric values and one decimal only
              }
              formik.setValues({
                ...values,
                quantity: parseFloat(inputValue) || null,
                quantityDisplay: inputValue
              })
            }}
            onBlur={formik.handleBlur}
            errorMessage={formik.errors?.quantity}
            isInvalid={formik.touched.quantity && !!formik.errors?.quantity}
          />
          <FormControl isInvalid={!!formik.errors?.amount}>
            <FormLabel color={!!formik.errors?.amount ? theme.colors.red[500] : 'black'}>
              Amount{' '}
              <Popover
                placement='top-start'
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [-5, 10]
                    }
                  }
                ]}
                trigger='click'
                defaultIsOpen={false}
              >
                <PopoverTrigger>
                  <ChakraButton
                    aria-label='intermediary state popover'
                    variant='link'
                    h='1rem'
                    d='inline-block'
                    minW='1rem'
                    lineHeight={0}
                    border={0}
                    rounded='full'
                  >
                    <Icon as={FaQuestionCircle} color='black'>
                      Trigger Popover
                    </Icon>
                  </ChakraButton>
                </PopoverTrigger>
                <PopoverContent color='black'>
                  <PopoverArrow />
                  <PopoverBody>
                    The total valuation amount for the journaled securities. This will be used for cost basis and
                    miscellaneous income reporting. e.g if 0.25 shares of a $10 security are being rewarded, the amount
                    should be $2.50.
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </FormLabel>
            <InputGroup>
              <InputLeftElement color='gray.500' opacity={1} pointerEvents='none' width='32px'>
                <Box>$</Box>
              </InputLeftElement>
              <Input
                name='amount'
                value={amountDisplay}
                isRequired
                onBlur={formik.handleBlur}
                paddingLeft={8}
                onChange={(e) => {
                  formik.setValues({
                    ...values,
                    amount: numberInputFormatter(e.target.value, numberFormatter).raw as number,
                    amountDisplay: numberInputFormatter(e.target.value, numberFormatter).display
                  })
                }}
                isInvalid={formik.touched.amount && !!formik.errors?.amount}
              />
            </InputGroup>
            <FormErrorMessage>{formik.errors?.amount}</FormErrorMessage>
          </FormControl>
        </Stack>
        <LabeledInput
          label='Source Account Description'
          charCounter={30}
          name='sourceAccountDescription'
          onBlur={formik.handleBlur}
          value={sourceAccountDescription}
          onChange={(e) => {
            formik.setFieldValue('sourceAccountDescription', e.target.value)
          }}
          errorMessage={formik.errors?.sourceAccountDescription}
          isInvalid={formik.touched.sourceAccountDescription && !!formik.errors?.sourceAccountDescription}
        />
        <LabeledInput
          label='Source Account Description 2'
          charCounter={30}
          name='sourceAccountDescription2'
          onBlur={formik.handleBlur}
          value={sourceAccountDescription2}
          onChange={(e) => {
            formik.setFieldValue('sourceAccountDescription2', e.target.value)
          }}
          errorMessage={formik.errors?.sourceAccountDescription2}
          isInvalid={formik.touched.sourceAccountDescription2 && !!formik.errors?.sourceAccountDescription2}
        />
        <LabeledInput
          label='Destination Account Description'
          charCounter={30}
          name='destinationAccountDescription'
          onBlur={formik.handleBlur}
          value={destinationAccountDescription}
          onChange={(e) => {
            formik.setFieldValue('destinationAccountDescription', e.target.value)
          }}
          errorMessage={formik.errors?.destinationAccountDescription}
          isInvalid={formik.touched.destinationAccountDescription && !!formik.errors?.destinationAccountDescription}
        />
        <LabeledInput
          label='Destination Account Description 2'
          charCounter={30}
          name='destinationAccountDescription2'
          onBlur={formik.handleBlur}
          value={destinationAccountDescription2}
          onChange={(e) => {
            formik.setFieldValue('destinationAccountDescription2', e.target.value)
          }}
          errorMessage={formik.errors?.destinationAccountDescription2}
          isInvalid={formik.touched.destinationAccountDescription2 && !!formik.errors?.destinationAccountDescription2}
        />
        <Box>
          <Button
            variant='primary'
            onClick={onOpen}
            isDisabled={!isValid || !isToAccountValid || hasNoFirmAccountAlert}
          >
            Next
          </Button>
        </Box>
        <PositionJournalPreviewModal
          fromAccountDisplayText={buildToAccountDisplayText(
            //This field requires the to account format
            formik.values.fromAccount!,
            accountDetails?.accountTitle!,
            'No account provided'
          )}
          toAccountDisplayText={buildToAccountDisplayText(
            formik.values.toAccount!,
            accountLookupTitle,
            'No account provided'
          )}
          onClose={onClose}
          isOpen={isOpen}
          hasSubmitPermission={hasSubmitPermission}
        />
      </Stack>
    </Box>
  )
}
export default PositionJournalForm
